import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, BookOpen, Clock, Home } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedSection } from "@/components/ui/animated-section";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getArticle, getCategory, helpArticles } from "@/data/helpArticleData";
import ReactMarkdown from "react-markdown";

export default function HelpArticle() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();
  
  // Get article and category data
  const article = category && slug ? getArticle(category, slug) : null;
  const categoryData = category ? getCategory(category) : null;
  
  // Set document meta for SEO
  useDocumentMeta({
    title: article ? `${article.title} - Help Center` : "Article Not Found - Help Center",
    description: article?.description || "Help article not found. Browse our help center for guides and documentation.",
  });
  
  // Handle 404 case
  if (!article || !categoryData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main>
          <Section className="pt-24 md:pt-32 pb-16">
            <Container>
              <AnimatedSection className="text-center max-w-2xl mx-auto">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="h-8 w-8 text-muted-foreground" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Article Not Found
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Sorry, we couldn't find the help article you're looking for. 
                  It may have been moved or removed.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/help">
                    <Button size="lg">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Help Center
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline">
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </AnimatedSection>
            </Container>
          </Section>
        </main>
        <Footer />
      </div>
    );
  }

  // Calculate estimated read time (roughly 200 words per minute)
  const wordCount = article.content.split(/\s+/).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Breadcrumb Navigation */}
        <Section className="pt-24 pb-4 border-b border-border">
          <Container>
            <nav className="flex items-center text-sm text-muted-foreground">
              <Link 
                to="/" 
                className="hover:text-foreground transition-colors flex items-center"
              >
                <Home className="h-4 w-4" />
              </Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <Link 
                to="/help" 
                className="hover:text-foreground transition-colors"
              >
                Help Center
              </Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <Link 
                to={`/help#${category}`}
                className="hover:text-foreground transition-colors"
              >
                {categoryData.title}
              </Link>
              <ChevronRight className="h-4 w-4 mx-2" />
              <span className="text-foreground font-medium truncate max-w-[200px]">
                {article.title}
              </span>
            </nav>
          </Container>
        </Section>

        {/* Article Header */}
        <Section className="pt-8 pb-6">
          <Container>
            <AnimatedSection className="max-w-3xl">
              <Button 
                variant="ghost" 
                size="sm" 
                className="mb-4 -ml-2"
                onClick={() => navigate("/help")}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Help Center
              </Button>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">
                  {categoryData.title}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {readTime} min read
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {article.title}
              </h1>
              
              <p className="text-lg text-muted-foreground">
                {article.description}
              </p>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Article Content */}
        <Section className="py-8">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <AnimatedSection>
                  <div className="prose prose-lg dark:prose-invert max-w-none">
                    <ReactMarkdown
                      components={{
                        h2: ({ children }) => (
                          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4 first:mt-0">
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">
                            {children}
                          </h3>
                        ),
                        p: ({ children }) => (
                          <p className="text-muted-foreground mb-4 leading-relaxed">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc pl-6 mb-4 space-y-2 text-muted-foreground">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal pl-6 mb-4 space-y-2 text-muted-foreground">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="text-muted-foreground">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-foreground">{children}</strong>
                        ),
                        code: ({ children, className }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono text-foreground">
                              {children}
                            </code>
                          ) : (
                            <code className="block p-4 bg-muted rounded-lg text-sm font-mono overflow-x-auto">
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children }) => (
                          <pre className="bg-muted rounded-lg p-4 overflow-x-auto mb-4">
                            {children}
                          </pre>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                            {children}
                          </blockquote>
                        ),
                        table: ({ children }) => (
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-border rounded-lg">
                              {children}
                            </table>
                          </div>
                        ),
                        th: ({ children }) => (
                          <th className="px-4 py-2 bg-muted text-left text-sm font-semibold text-foreground border-b border-border">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="px-4 py-2 text-sm text-muted-foreground border-b border-border">
                            {children}
                          </td>
                        ),
                      }}
                    >
                      {article.content}
                    </ReactMarkdown>
                  </div>
                </AnimatedSection>
                
                {/* Article Footer */}
                <AnimatedSection delay={0.1}>
                  <div className="mt-12 pt-8 border-t border-border">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Was this article helpful?
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          Yes, it helped
                        </Button>
                        <Link to="/contact">
                          <Button variant="outline" size="sm">
                            I need more help
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
              
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  {/* Related Articles */}
                  {article.relatedArticles && article.relatedArticles.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Related Articles</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <ul className="space-y-2">
                          {article.relatedArticles.map((related, index) => (
                            <li key={index}>
                              <Link
                                to={`/help/${related.category}/${related.slug}`}
                                className="flex items-start text-sm text-muted-foreground hover:text-primary transition-colors group"
                              >
                                <ChevronRight className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                                <span>{related.title}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Other Articles in Category */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">More in {categoryData.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2">
                        {categoryData.articles
                          .filter((a) => a.slug !== slug)
                          .slice(0, 5)
                          .map((otherArticle, index) => (
                            <li key={index}>
                              <Link
                                to={`/help/${category}/${otherArticle.slug}`}
                                className="flex items-start text-sm text-muted-foreground hover:text-primary transition-colors group"
                              >
                                <ChevronRight className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                                <span>{otherArticle.title}</span>
                              </Link>
                            </li>
                          ))}
                      </ul>
                    </CardContent>
                  </Card>
                  
                  {/* Need Help CTA */}
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-foreground mb-2">
                        Still need help?
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Our support team is here to assist you.
                      </p>
                      <Link to="/contact">
                        <Button size="sm" className="w-full">
                          Contact Support
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </Container>
        </Section>

        {/* Browse More Categories */}
        <Section background="muted" className="py-12">
          <Container>
            <AnimatedSection className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Browse Other Categories
              </h2>
              <p className="text-muted-foreground">
                Explore more help topics
              </p>
            </AnimatedSection>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.values(helpArticles)
                .filter((cat) => cat.id !== category)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/help#${cat.id}`}
                    className="p-4 rounded-lg bg-background border hover:border-primary/50 hover:shadow-sm transition-all text-center group"
                  >
                    <h3 className="font-medium text-foreground text-sm group-hover:text-primary transition-colors">
                      {cat.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {cat.articles.length} articles
                    </p>
                  </Link>
                ))}
            </div>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
